import React, { useMemo } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { IconExternalLink } from '@tabler/icons'
import { Grid } from '@mantine/core'
import useStyles from './SubscriptionBasedVsTraditional.styles'
import { translate } from '../../../../utilities/language'
import { getAttribution } from '../../../../utilities/content'
import {
  ARTICLE_IMAGE_AVIF,
  ARTICLE_IMAGE_JPEG,
  SUBSCRIPTION_BASED_VS_TRADITIONAL_TITLE_HTML,
  SUBSCRIPTION_BASED_VS_TRADITIONAL_TITLE_HTML_ES,
  SUBSCRIPTION_BASED_VS_TRADITIONAL_TITLE_HTML_FR,
  SUBSCRIPTION_BASED_VS_TRADITIONAL_TITLE_HTML_KO,
  SUBSCRIPTION_BASED_VS_TRADITIONAL_ARTICLE_PUBLISHED_STR,
} from '../../../../static-data/subscription-based-vs-traditional-data'
import {
  MAIN_SITE_ENVIRONMENT_HOST,
  MAIN_SITE_ENVIRONMENT_HOST_ES,
  MAIN_SITE_ENVIRONMENT_HOST_FR,
  MAIN_SITE_ENVIRONMENT_HOST_KO,
} from '../../../../utilities/config'
import {
  FIVE_MISTAKES_ARTICLE_URL,
  FIVE_MISTAKES_ARTICLE_URL_ES,
  FIVE_MISTAKES_ARTICLE_URL_FR,
  FIVE_MISTAKES_ARTICLE_URL_KO,
} from '../../../../static-data/five-mistakes-businesses-make-data'

const BackToArticle: React.ComponentType<any> = dynamic(
  () => import('../../../controls/BackToArticle'),
  {
    ssr: true,
  }
)
const ReadyToTransform: React.ComponentType<any> = dynamic(
  () => import('../../../controls/ReadyToTransform'),
  {
    ssr: true,
  }
)

export default function SubscriptionBasedVsTraditional(props: any) {
  const { classes } = useStyles()
  const selectedLanguage: any = props?.selectedLanguage
  const avifCompatible = props?.avifCompatible

  const articleImage = useMemo(() => {
    return (
      <Image
        width={512}
        height={279}
        sizes="(max-width: 350px) 256px, 
               (max-width: 430px) 300px, 
               (max-width: 590px) 384px, 
               512px"
        src={avifCompatible ? ARTICLE_IMAGE_AVIF : ARTICLE_IMAGE_JPEG}
        alt=""
        priority={true}
        style={{
          marginTop: '30px',
          borderRadius: '10px',
          display: 'block',
          width: '100%',
          maxWidth: '512px',
          height: 'auto',
        }}
      />
    )
  }, [avifCompatible])

  return (
    <div className={classes.mainGrid}>
      <div className={classes.innerDiv}>
        <Grid columns={24}>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <h1 className={classes.standardText + ' ' + classes.h1Tag}>
                {translate(
                  SUBSCRIPTION_BASED_VS_TRADITIONAL_TITLE_HTML,
                  SUBSCRIPTION_BASED_VS_TRADITIONAL_TITLE_HTML_ES,
                  SUBSCRIPTION_BASED_VS_TRADITIONAL_TITLE_HTML_FR,
                  SUBSCRIPTION_BASED_VS_TRADITIONAL_TITLE_HTML_KO,
                  selectedLanguage
                )}
              </h1>
              {getAttribution(
                translate(
                  'Editor',
                  'Editora',
                  'Éditrice',
                  '편집자',
                  selectedLanguage
                ),
                SUBSCRIPTION_BASED_VS_TRADITIONAL_ARTICLE_PUBLISHED_STR,
                selectedLanguage,
                false
              )}
              {articleImage}
            </div>
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <h2 className={classes.subSection}>
              {translate(
                <>Why Music Licensing Matters for Businesses</>,
                <>
                  Por qué la concesión de licencias musicales es importante para
                  las empresas
                </>,
                <>
                  Pourquoi la gestion des droits musicaux est importante pour
                  les entreprises
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    기업에게
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음악
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    라이선스가
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    중요한
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이유
                  </span>
                </>,
                selectedLanguage
              )}
            </h2>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  Playing music in your business isn’t as simple as hitting
                  “play” on Spotify. Whether you run a coffee shop, gym,
                  restaurant, or retail store, using music publicly requires
                  proper licensing. Without it, you face serious legal
                  risks&nbsp;— including lawsuits and fines up to $150,000 per
                  violation.
                </>,
                <>
                  Reproducir música en tu negocio no es tan sencillo como darle
                  al botón de "reproducir" en Spotify. Ya sea que tengas una
                  cafetería, un gimnasio, un restaurante o una tienda, el uso
                  público de música requiere la licencia correspondiente. Sin
                  ella, te expones a graves riesgos legales, incluyendo demandas
                  y multas de hasta 150.000 dólares por infracción.
                </>,
                <>
                  Diffuser de la musique dans votre établissement ne se résume
                  pas à appuyer sur le bouton «&nbsp;lecture&nbsp;» ​​de
                  Spotify. Que vous teniez un café, une salle de sport, un
                  restaurant ou un magasin, la diffusion publique de musique
                  nécessite une licence appropriée. Sans cela, vous vous exposez
                  à de sérieux risques juridiques, notamment des poursuites
                  judiciaires et des amendes pouvant atteindre
                  150&nbsp;000&nbsp;$ par infraction.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    사업장에서
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음악을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    트는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    것은
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    스포티파이에서
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    "재생"
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    버튼을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    누르는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    것만큼
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    간단하지
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    않습니다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    커피숍,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    헬스장,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    레스토랑
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    또는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    소매점을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    운영하든
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    관계없이,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    공개적으로
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음악을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    사용하려면
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    적절한
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    라이선스가
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    필요합니다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    라이선스
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    없이는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    심각한
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    법적
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    위험에
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    직면할
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    수
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    있으며,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    여기에는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    소송
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    및
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    위반
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    건당
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    최대
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    15만
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    달러의
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    벌금이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    포함됩니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  The good news? You have options. Understanding the difference
                  between subscription-based and traditional{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={MAIN_SITE_ENVIRONMENT_HOST}
                    title={
                      'Fully licensed commercial background music for your business - Practical Stream'
                    }
                    rel="noopener noreferrer"
                  >
                    music{' '}
                    <span className={classes.spanPre}>
                      licensing&nbsp;
                      <IconExternalLink />
                    </span>
                  </a>{' '}
                  will help you choose the right solution, stay compliant, and
                  avoid costly mistakes.
                </>,
                <>
                  ¿La buena noticia? Tienes opciones. Comprender la diferencia
                  entre las{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={MAIN_SITE_ENVIRONMENT_HOST_ES}
                    title={
                      'Música de fondo comercial con licencia completa para su negocio - Practical Stream'
                    }
                    rel="noopener noreferrer"
                  >
                    licencias de{' '}
                    <span className={classes.spanPre}>
                      música&nbsp;
                      <IconExternalLink />
                    </span>
                  </a>{' '}
                  por suscripción y las licencias tradicionales te ayudará a
                  elegir la solución adecuada, a cumplir con las obligaciones
                  legales y a evitar errores costosos.
                </>,
                <>
                  La bonne nouvelle ? Vous avez le choix. Comprendre la
                  différence entre les{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={MAIN_SITE_ENVIRONMENT_HOST_FR}
                    title={
                      "Musique d'ambiance commerciale sous licence complète pour votre entreprise - Practical Stream"
                    }
                    rel="noopener noreferrer"
                  >
                    licences{' '}
                    <span className={classes.spanPre}>
                      musicales&nbsp;
                      <IconExternalLink />
                    </span>
                  </a>{' '}
                  par abonnement et les licences traditionnelles vous aidera à
                  choisir la solution adaptée, à respecter la réglementation et
                  à éviter des erreurs coûteuses.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    좋은
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    소식은
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    선택지가
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    있다는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    것입니다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    구독
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    기반
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음악
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    라이선스와
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    기존
                  </span>{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={MAIN_SITE_ENVIRONMENT_HOST_KO}
                    title={
                      '귀사의 사업장을 위한 정식 라이선스를 획득한 상업용 배경 음악입니다. - Practical Stream'
                    }
                    rel="noopener noreferrer"
                  >
                    음악{' '}
                    <span className={classes.spanPre}>
                      라이선스의&nbsp;
                      <IconExternalLink />
                    </span>
                  </a>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    차이점을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이해하면
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    올바른
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    솔루션을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    선택하고
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    규정을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    준수하며
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    비용이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    많이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    드는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    실수를
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    피할
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    수
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    있습니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <section id="subscription-based-b2b-music-licensing-defined">
              <h2 className={classes.subSection}>
                {translate(
                  <>What Is Subscription-Based Music Licensing?</>,
                  <>
                    ¿Qué es la concesión de licencias de música por suscripción?
                  </>,
                  <>Qu'est-ce que la licence musicale par abonnement&nbsp;?</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      구독
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      기반
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선싱이란
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      무엇인가요?
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Subscription-based music licensing services work like
                    Netflix for business music. You pay a monthly or annual fee,
                    and in return, you get unlimited access to licensed music
                    for commercial use. These services are offered by music
                    licensing companies for businesses like Practical Stream,
                    Soundtrack Your Brand, Cloud Cover Music, and Mood Media.
                  </>,
                  <>
                    Los servicios de licencias de música por suscripción
                    funcionan como Netflix para la música comercial. Se paga una
                    tarifa mensual o anual y, a cambio, se obtiene acceso
                    ilimitado a música con licencia para uso comercial. Estos
                    servicios son ofrecidos por empresas de licencias musicales
                    para negocios como Practical Stream, Soundtrack Your Brand,
                    Cloud Cover Music y Mood Media.
                  </>,
                  <>
                    Les services de licence musicale par abonnement fonctionnent
                    comme Netflix pour la musique d'entreprise. Vous payez un
                    abonnement mensuel ou annuel et bénéficiez en échange d'un
                    accès illimité à de la musique sous licence pour un usage
                    commercial. Ces services sont proposés par des sociétés de
                    licence musicale pour les entreprises, telles que Practical
                    Stream, Soundtrack Your Brand, Cloud Cover Music et Mood
                    Media.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      구독
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      기반
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      서비스는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비즈니스용
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      위한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      넷플릭스와
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      같습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      월별
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      또는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      연간
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      요금을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지불하면
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      상업적
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      용도로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사용할
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      수
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      무제한으로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      접근할
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      수
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이러한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      서비스는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Practical
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Stream,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Soundtrack
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Your
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Brand,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Cloud
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Cover
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Music,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Mood
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Media와
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      같은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      회사에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      기업을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대상으로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      제공합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The subscription-based music licensing agreement typically
                    covers all the legal rights you need&nbsp;— performance
                    rights, mechanical rights, and synchronization rights&nbsp;—
                    bundled into one simple payment.
                  </>,
                  <>
                    El acuerdo de licencia de música por suscripción suele
                    cubrir todos los derechos legales necesarios: derechos de
                    ejecución pública, derechos de reproducción mecánica y
                    derechos de sincronización, todo ello incluido en un único
                    pago.
                  </>,
                  <>
                    L'accord de licence musicale par abonnement couvre
                    généralement tous les droits légaux dont vous avez
                    besoin&nbsp;— droits d'exécution, droits de reproduction
                    mécanique et droits de synchronisation&nbsp;— regroupés en
                    un seul paiement simple.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      구독
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      기반
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      계약은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      일반적으로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      공연권,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      복제권,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      동기화권
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      등
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      필요한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      모든
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      법적
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      권리를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하나의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      간단한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      결제로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      제공합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="traditional-b2b-music-licensing-defined">
              <h2 className={classes.subSection}>
                {translate(
                  <>What Is Traditional Music Licensing?</>,
                  <>¿Qué es la concesión de licencias de música tradicional?</>,
                  <>Qu'est-ce que la licence musicale traditionnelle&nbsp;?</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전통적인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선싱이란
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      무엇일까요?
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Traditional music licensing requires you to obtain separate
                    licenses for each type of music use. You might need licenses
                    from performing rights organizations (PROs) like ASCAP, BMI,
                    or SESAC. If you want a specific popular song, you pay per
                    track or negotiate individual agreements.
                  </>,
                  <>
                    La concesión de licencias de música tradicional requiere
                    obtener licencias separadas para cada tipo de uso musical.
                    Es posible que necesite licencias de organizaciones de
                    derechos de autor (PRO, por sus siglas en inglés) como
                    ASCAP, BMI o SESAC. Si desea una canción popular específica,
                    paga por canción o negocia acuerdos individuales.
                  </>,
                  <>
                    L'octroi de licences musicales traditionnelles exige
                    l'obtention de licences distinctes pour chaque type
                    d'utilisation musicale. Vous pourriez avoir besoin de
                    licences auprès d'organismes de gestion des droits
                    d'exécution (PRO) tels qu'ASCAP, BMI ou SESAC. Si vous
                    souhaitez utiliser une chanson populaire spécifique, vous
                    payez par morceau ou négociez des accords individuels.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전통적인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      방식에서는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      각
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사용
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      유형별로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      별도의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      취득해야
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      합니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      ASCAP,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      BMI
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      또는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      SESAC와
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      같은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      저작권
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      관리
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      단체(PRO)로부터
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      받아야
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      할
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      수도
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      특정
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      인기곡을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사용하려면
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      곡당
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비용을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지불하거나
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      개별
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      계약을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      협상해야
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    This is one of the different types of music licensing that
                    works well for one-time projects, but can become complicated
                    and expensive for ongoing commercial use.
                  </>,
                  <>
                    Este es uno de los diferentes tipos de licencias musicales
                    que funciona bien para proyectos puntuales, pero que puede
                    volverse complicado y costoso para un uso comercial
                    continuo.
                  </>,
                  <>
                    Il s'agit de l'un des différents types de licences musicales
                    qui existent.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이것은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      다양한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      유형
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      중
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하나로,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      일회성
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      프로젝트에는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      적합하지만
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지속적인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      상업적
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      용도로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사용될
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      경우
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      복잡하고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비용이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      많이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      들
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      수
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="traditional-vs-subscription-costs">
              <h2 className={classes.subSection}>
                {translate(
                  <>Cost Structure Comparison</>,
                  <>Comparación de la estructura de costos</>,
                  <>Comparaison des structures de coûts</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비용
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구조
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비교
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <section id="subscription-based-costs">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Subscription-Based:
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Basado en suscripción:
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Basé sur un abonnement&nbsp;:
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        구독
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        기반:
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Flat monthly fee (typically $25–$50 per location for
                        small businesses)
                      </>,
                      <>
                        Tarifa mensual fija (normalmente entre 25 y 50 dólares
                        por ubicación para pequeñas empresas)
                      </>,
                      <>
                        Forfait mensuel fixe (généralement de 25&nbsp;à&nbsp;50
                        dollars par site pour les petites entreprises)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          고정
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          월별
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          요금
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          (일반적으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소규모
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사업체의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경우
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지점당
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          25달러~50달러)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Predictable, budget-friendly pricing</>,
                      <>Precios predecibles y asequibles.</>,
                      <>Tarifs prévisibles et abordables.</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          예측
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가능하고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          예산
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          친화적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가격
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          책정
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>No surprise costs or renewal negotiations</>,
                      <>
                        Sin costes inesperados ni negociaciones de renovación.
                      </>,
                      <>
                        Pas de coûts imprévus ni de négociations de
                        renouvellement.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          예상치
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          못한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          추가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          비용이나
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          갱신
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          협상
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          없이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이용
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가능합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="traditional-costs">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Traditional Licensing:
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Licencias tradicionales:
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Licences traditionnelles&nbsp;:
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전통적인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        라이선스
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        방식:
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Multiple separate fees to different PROs</>,
                      <>
                        Múltiples tarifas separadas a diferentes organizaciones
                        de gestión de derechos de autor.
                      </>,
                      <>
                        Plusieurs frais distincts à verser à différentes
                        sociétés de gestion des droits d'auteur.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          여러
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          저작권
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          관리
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          단체에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          각각
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          다른
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수수료를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지불해야
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Per-track costs for specific songs (can range from $50
                        to thousands)
                      </>,
                      <>
                        Costos por pista para canciones específicas (pueden
                        oscilar entre $50 y miles de dólares)
                      </>,
                      <>
                        Coûts par morceau pour des chansons spécifiques (pouvant
                        varier de 50&nbsp;$ à plusieurs milliers de dollars)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          특정
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          곡에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          트랙별
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          비용(50달러에서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수천
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          달러까지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          다양할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있습니다)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Variable pricing based on square footage, revenue, or
                        audience size
                      </>,
                      <>
                        Precios variables basados ​en los metros cuadrados, los
                        ingresos o el tamaño de la audiencia.
                      </>,
                      <>
                        Tarification variable basée sur la superficie, le
                        chiffre d'affaires ou la taille de l'audience.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          면적,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          매출
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          또는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시청자
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          규모에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          따라
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          달라지는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          변동
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가격
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          책정
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          방식
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    For most businesses, subscriptions offer better value and
                    transparency.
                  </>,
                  <>
                    Para la mayoría de las empresas, las suscripciones ofrecen
                    una mejor relación calidad-precio y mayor transparencia.
                  </>,
                  <>
                    Pour la plupart des entreprises, les abonnements offrent un
                    meilleur rapport qualité-prix et une plus grande
                    transparence.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대부분의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      기업에게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구독
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      서비스는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      더
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      나은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      가치와
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      투명성을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      제공합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="ease-of-use-and-management">
              <h2 className={classes.subSection}>
                {translate(
                  <>Ease of Use & Management</>,
                  <>Facilidad de uso y gestión</>,
                  <>Facilité d'utilisation et de gestion</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      편의성
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      및
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      관리
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      용이성
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Subscription-based music licensing companies handle
                    everything for you. You get:
                  </>,
                  <>
                    Las empresas de licencias de música por suscripción se
                    encargan de todo por usted. Usted obtiene:
                  </>,
                  <>
                    Les sociétés de licences musicales par abonnement s'occupent
                    de tout pour vous. Vous bénéficiez de&nbsp;:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      구독
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      기반
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      회사는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      모든
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      알아서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      처리해
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      드립니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      다음과
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      같은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      혜택을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      누리실
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      수
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>Pre-curated playlists for your industry</>,
                    <>
                      Listas de reproducción preseleccionadas para tu sector.
                    </>,
                    <>
                      Listes de lecture pré-sélectionnées pour votre secteur
                      d'activité.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        귀하의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        업종에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        맞춰
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        미리
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        선별된
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        플레이리스트
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Cloud-based platforms accessible from any device</>,
                    <>
                      Plataformas basadas en la nube accesibles desde cualquier
                      dispositivo.
                    </>,
                    <>
                      Plateformes basées sur le cloud, accessibles depuis
                      n'importe quel appareil.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        어떤
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        기기에서든
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        접속
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        가능한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        클라우드
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        기반
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        플랫폼
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Automatic updates and new music are added regularly</>,
                    <>
                      Las actualizaciones automáticas y la nueva música se
                      añaden regularmente.
                    </>,
                    <>
                      Des mises à jour automatiques et de nouvelles musiques
                      sont ajoutées régulièrement.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자동
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        업데이트가
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        제공되며
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        새로운
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        정기적으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        추가됩니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Zero paperwork or legal negotiations</>,
                    <>Sin papeleo ni negociaciones legales.</>,
                    <>
                      Aucune formalité administrative ni négociation juridique.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        서류
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        작업이나
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        법적
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        협상
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        절차가
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        전혀
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        없습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Traditional licensing requires you to manage multiple vendor
                    relationships, track renewal dates, and ensure each license
                    covers your actual usage. For busy business owners, this
                    administrative burden is a major drawback.
                  </>,
                  <>
                    El sistema de licencias tradicional requiere gestionar
                    múltiples relaciones con proveedores, hacer un seguimiento
                    de las fechas de renovación y asegurarse de que cada
                    licencia cubra el uso real. Para los empresarios con poco
                    tiempo, esta carga administrativa representa una desventaja
                    importante.
                  </>,
                  <>
                    Les systèmes de licences traditionnels vous obligent à gérer
                    de multiples relations avec les fournisseurs, à suivre les
                    dates de renouvellement et à vous assurer que chaque licence
                    couvre votre utilisation réelle. Pour les chefs d'entreprise
                    très occupés, cette charge administrative représente un
                    inconvénient majeur.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기존의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      방식은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      여러
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      공급업체와의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      관계를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      관리하고,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      갱신일을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      추적하며,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      각
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      실제
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사용량을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      제대로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      반영하는지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      확인해야
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      번거로움이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      바쁜
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사업주들에게
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이러한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      관리
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      부담은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      큰
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      단점입니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="scalability-comparison">
              <h2 className={classes.subSection}>
                {translate(
                  <>Scalability for Growing Businesses</>,
                  <>Escalabilidad para empresas en crecimiento</>,
                  <>Évolutivité pour les entreprises en croissance</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      성장하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      기업을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      위한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      확장성
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Music licensing companies for restaurants, retail chains,
                    and gyms prefer subscriptions because they scale easily. Add
                    a new location, and you simply update your subscription.
                    Enterprise pricing often includes volume discounts.
                  </>,
                  <>
                    Las empresas de licencias musicales para restaurantes,
                    cadenas minoristas y gimnasios prefieren las suscripciones
                    porque son fácilmente escalables. Si se añade una nueva
                    ubicación, simplemente se actualiza la suscripción. Los
                    precios para empresas suelen incluir descuentos por volumen.
                  </>,
                  <>
                    Les sociétés de gestion des droits musicaux pour les
                    restaurants, les chaînes de magasins et les salles de sport
                    privilégient les abonnements car ils sont facilement
                    adaptables. Pour ajouter un nouvel établissement, il suffit
                    de mettre à jour son abonnement. Les tarifs pour les
                    entreprises incluent souvent des réductions pour les gros
                    volumes.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      레스토랑,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      소매
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      체인점,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      헬스장
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      등을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      위한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      제공
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      업체들은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구독
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      방식을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      선호합니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구독
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      방식은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      확장이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      용이하기
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      때문입니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      새로운
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지점을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      추가할
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      경우,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구독
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      정보를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      업데이트하기만
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하면
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      됩니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      기업용
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      요금제에는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대량
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구매
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      시
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      할인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      혜택이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      포함되는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      경우가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      많습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="legal-coverage-and-compliance">
              <h2 className={classes.subSection}>
                {translate(
                  <>Legal Coverage & Compliance</>,
                  <>Cobertura legal y cumplimiento normativo</>,
                  <>Couverture juridique et conformité</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      법률
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      자문
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      및
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      규정
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      준수
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <section id="music-licensing-agreement-coverage">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      Subscription services provide comprehensive legal
                      coverage. Your agreement typically includes:
                    </>,
                    <>
                      Los servicios de suscripción brindan cobertura legal
                      integral. Su contrato generalmente incluye:
                    </>,
                    <>
                      Les services d'abonnement offrent une couverture juridique
                      complète. Votre contrat comprend généralement&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        구독
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        서비스는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        포괄적인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        법률
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        서비스를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        제공합니다. 일반적으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        계약에는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        다음
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사항이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        포함됩니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Public performance rights</>,
                      <>Derechos de ejecución pública</>,
                      <>Droits d'exécution publique</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          공연권
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Mechanical rights</>,
                      <>Derechos mecánicos</>,
                      <>Droits mécaniques</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          기계적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          권리
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Coverage for in-store, on-hold, and sometimes digital
                        use
                      </>,
                      <>
                        Cobertura para uso en tienda, en espera y, en ocasiones,
                        digital.
                      </>,
                      <>
                        Couverture pour une utilisation en magasin, en attente
                        téléphonique et parfois numérique.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          매장
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          내
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대기
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          중
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          그리고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경우에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          따라
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          디지털
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          보장
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Legal protection during audits</>,
                      <>Protección legal durante las auditorías.</>,
                      <>Protection juridique pendant les audits.</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          감사
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          과정
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          중
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          법적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          보호
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Traditional licensing leaves gaps. You might have a BMI
                    license but forget ASCAP, or cover in-store music but not
                    your website or social media videos. These gaps expose you
                    to lawsuits.
                  </>,
                  <>
                    El sistema de licencias tradicional presenta lagunas. Puede
                    que tengas una licencia de BMI, pero te olvides de ASCAP, o
                    que cubras la música que suena en tu tienda física, pero no
                    la de tu sitio web o tus vídeos en redes sociales. Estas
                    lagunas te exponen a posibles demandas.
                  </>,
                  <>
                    Les licences traditionnelles présentent des lacunes. Vous
                    pourriez avoir une licence BMI, mais oublier ASCAP, ou
                    couvrir la musique diffusée en magasin, mais pas celle de
                    votre site web ou de vos vidéos sur les réseaux sociaux. Ces
                    lacunes vous exposent à des poursuites judiciaires.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기존의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      방식에는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      허점이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      BMI
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있지만
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      ASCAP
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      잊어버리거나,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      매장
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      내
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사용에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있지만
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      웹사이트나
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      소셜
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      미디어
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      영상에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      없는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      경우가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      수
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있습니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이러한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      허점은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      소송으로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이어질
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      수
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="best-choice-by-business-type">
              <h2 className={classes.subSection}>
                {translate(
                  <>Best Fit by Business Type</>,
                  <>La mejor opción según el tipo de negocio</>,
                  <>Meilleure solution par type d'entreprise</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      업종별
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      최적의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      솔루션
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <section id="when-subscription-based-works-better">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>Subscription-Based Works Best For:</>,
                    <>El modelo de suscripción funciona mejor para:</>,
                    <>
                      Le modèle d'abonnement convient particulièrement bien aux
                      cas suivants&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        구독
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        기반
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        방식이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        가장
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        적합한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        경우는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        다음과
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        같습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Retail stores play background music daily</>,
                      <>
                        Las tiendas minoristas ponen música de fondo a diario.
                      </>,
                      <>
                        Les magasins diffusent de la musique d'ambiance tous les
                        jours.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          소매점들은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          매일
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          배경
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          틀어놓습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Music licensing companies for restaurants recommend
                        subscriptions for dining establishments
                      </>,
                      <>
                        Las empresas de licencias musicales para restaurantes
                        recomiendan suscripciones para establecimientos de
                        restauración.
                      </>,
                      <>
                        Les sociétés de gestion des droits musicaux pour les
                        restaurants recommandent des abonnements pour les
                        établissements de restauration.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          레스토랑을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          라이선스
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          회사는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          외식업체를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대상으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구독
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          서비스를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          추천합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Gyms, salons, spas, and medical offices</>,
                      <>
                        Gimnasios, salones de belleza, spas y consultorios
                        médicos.
                      </>,
                      <>
                        Salles de sport, salons de coiffure, spas et cabinets
                        médicaux.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          헬스장,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          미용실,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          스파,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          그리고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          병원
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Multi-location businesses and franchises</>,
                      <>Empresas con múltiples ubicaciones y franquicias</>,
                      <>Entreprises multi-sites et franchises</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          여러
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지점을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          운영하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기업
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          및
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          프랜차이즈
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Companies needing music for apps, websites, or digital
                        channels
                      </>,
                      <>
                        Empresas que necesitan música para aplicaciones, sitios
                        web o canales digitales.
                      </>,
                      <>
                        Entreprises ayant besoin de musique pour leurs
                        applications, sites web ou canaux numériques.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          앱,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          웹사이트
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          또는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          디지털
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          채널에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          필요한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기업
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="when-traditional-works-better">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>Traditional Licensing Works Best For:</>,
                    <>
                      La concesión de licencias tradicionales funciona mejor
                      para:
                    </>,
                    <>
                      Le système de licences traditionnelles convient le mieux
                      aux cas suivants :
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전통적인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        라이선스
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        방식이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        가장
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        적합한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        경우는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        다음과
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        같습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Film and advertising agencies need specific popular
                        songs
                      </>,
                      <>
                        Las productoras de cine y las agencias de publicidad
                        necesitan canciones populares específicas.
                      </>,
                      <>
                        Les agences de cinéma et de publicité ont besoin de
                        chansons populaires spécifiques.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          영화
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          제작사와
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          광고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대행사는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          특정
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          인기곡들이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          필요합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>One-time campaigns or events</>,
                      <>Campañas o eventos puntuales</>,
                      <>Campagnes ou événements ponctuels</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          일회성
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          캠페인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          또는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이벤트
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Projects requiring classical music licensing for
                        specific compositions
                      </>,
                      <>
                        Proyectos que requieren la obtención de licencias de
                        música clásica para composiciones específicas.
                      </>,
                      <>
                        Projets nécessitant l'obtention de licences pour des
                        œuvres de musique classique spécifiques.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          특정
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작곡가의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          작품에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          클래식
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          라이선스가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          필요한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          프로젝트
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Businesses with highly specialized music needs</>,
                      <>
                        Empresas con necesidades musicales altamente
                        especializadas.
                      </>,
                      <>
                        Entreprises ayant des besoins musicaux hautement
                        spécialisés.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          고도로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전문화된
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          관련
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          요구
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사항을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가진
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기업
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
            </section>
            <section id="five-signs-you-need-subscription-based">
              <h2 className={classes.subSection}>
                {translate(
                  <>
                    5 Signs Your Business Needs a Music Licensing Subscription
                  </>,
                  <>
                    5 señales de que tu negocio necesita una suscripción de
                    licencias musicales.
                  </>,
                  <>
                    5 signes indiquant que votre entreprise a besoin d'un
                    abonnement de licence musicale.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      귀사의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비즈니스에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구독이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      필요한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      5가지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      징후
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <section id="sign-1-playing-music-daily">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Sign 1: You Play Music Daily in Public or Commercial
                        Spaces
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Señal 1: Escuchas música a diario en espacios públicos o
                        comerciales.
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Signe 1&nbsp;: Vous diffusez de la musique
                        quotidiennement dans des lieux publics ou commerciaux.
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        징후
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        #1:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        당신은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        공공장소나
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        상업
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        공간에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        매일
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        틀어놓습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      If music is a constant in your café, boutique, or office
                      lobby, a subscription makes sense. Paying per track or
                      managing multiple licenses becomes impractical for
                      everyday use.
                    </>,
                    <>
                      Si la música es una constante en su cafetería, boutique o
                      vestíbulo de oficina, una suscripción es la opción más
                      lógica. Pagar por cada canción o gestionar varias
                      licencias resulta poco práctico para el uso diario.
                    </>,
                    <>
                      Si la musique est omniprésente dans votre café, votre
                      boutique ou le hall d'entrée de vos bureaux, un abonnement
                      est la solution idéale. Payer à la piste ou gérer
                      plusieurs licences devient vite impraticable au quotidien.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        카페,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        부티크
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        또는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사무실
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        로비에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        항상
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        흘러나온다면
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        구독
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        서비스가
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        합리적인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        선택입니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        곡당
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        비용을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        지불하거나
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        여러
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        라이선스를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        관리하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        일상적인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사용에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있어서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        비효율적입니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id="sign-2-multiple-locations-or-channels">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Sign 2: You Operate Multiple Locations or Digital
                        Channels
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Señal 2: Gestionas múltiples ubicaciones o canales
                        digitales.
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Signe 2&nbsp;: Vous gérez plusieurs sites physiques ou
                        canaux numériques.
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        징후
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        2:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        여러
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        지점이나
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        디지털
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        채널을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        운영하고
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Subscriptions scale seamlessly across physical locations,
                      websites, social media, and apps. Traditional licenses
                      require separate agreements for each use case.
                    </>,
                    <>
                      Las suscripciones se adaptan sin problemas a diferentes
                      ubicaciones físicas, sitios web, redes sociales y
                      aplicaciones. Las licencias tradicionales requieren
                      acuerdos separados para cada caso de uso.
                    </>,
                    <>
                      Les abonnements s'adaptent facilement à tous les
                      supports&nbsp;: points de vente physiques, sites web,
                      réseaux sociaux et applications. Les licences
                      traditionnelles, quant à elles, nécessitent des contrats
                      distincts pour chaque utilisation.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        구독
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        방식은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실제
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        매장,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        웹사이트,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        소셜
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        미디어
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        및
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        앱
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        등
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        다양한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        플랫폼에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        원활하게
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        확장됩니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        반면
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        기존
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        라이선스는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        각
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사용
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사례별로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        별도의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        계약이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        필요합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id="sign-3-you-want-predictable-costs">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Sign 3: You Want Predictable Monthly Costs
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Señal 3: Deseas tener costos mensuales predecibles.
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Signe 3&nbsp;: Vous souhaitez des coûts mensuels
                        prévisibles.
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        세
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        번째
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        신호:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        당신은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        예측
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        가능한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        월별
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        비용을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        원합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Budgeting is easier with a flat monthly fee. Traditional
                      licensing involves unpredictable renewal rates and
                      variable costs based on revenue or location size.
                    </>,
                    <>
                      El presupuesto es más fácil de gestionar con una tarifa
                      mensual fija. El sistema de licencias tradicional implica
                      tarifas de renovación impredecibles y costos variables que
                      dependen de los ingresos o del tamaño de la ubicación.
                    </>,
                    <>
                      La budgétisation est simplifiée grâce à un forfait mensuel
                      fixe. Les licences traditionnelles impliquent des frais de
                      renouvellement imprévisibles et des coûts variables en
                      fonction du chiffre d'affaires ou de la taille de
                      l'entreprise.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정액
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        월별
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        요금제를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이용하면
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        예산
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        관리가
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        더
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        쉬워집니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        기존
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        라이선스
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        방식은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        예측하기
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        어려운
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        갱신
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        요금과
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        매출
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        또는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사업장
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        규모에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        따라
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        변동되는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        비용이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        발생합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id="sign-4-you-lack-time-or-legal-expertise">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Sign 4: You Lack Time or Legal Expertise
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Señal 4: Careces de tiempo o de conocimientos legales.
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Signe 4&nbsp;: Vous manquez de temps ou d'expertise
                        juridique.
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        징후 4: 시간이나 법률 전문 지식이 부족합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Most business owners aren’t music licensing experts.
                      Subscriptions eliminate the need to understand complex PRO
                      agreements, negotiate with music licensing companies, or
                      track expiration dates.
                    </>,
                    <>
                      La mayoría de los dueños de negocios no son expertos en
                      licencias musicales. Las suscripciones eliminan la
                      necesidad de comprender los complejos acuerdos de las
                      sociedades de gestión de derechos de autor, negociar con
                      empresas de licencias musicales o estar pendientes de las
                      fechas de vencimiento.
                    </>,
                    <>
                      La plupart des chefs d'entreprise ne sont pas des experts
                      en licences musicales. Les abonnements éliminent la
                      nécessité de comprendre des accords complexes de gestion
                      collective, de négocier avec les sociétés de gestion
                      collective ou de suivre les dates d'expiration.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        대부분의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사업주들은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        저작권
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        전문가가
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        아닙니다. 구독
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        서비스를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이용하면
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        복잡한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        저작권
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        관리
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        단체(PRO)
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        계약을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이해하거나,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        라이선스
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        회사와
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        협상하거나,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        만료일을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        추적할
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        필요가
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        없습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id="sign-5-you-want-low-risk-compliant-music">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Sign 5: You Want Low-Risk, Always-Compliant Music
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Señal 5: Buscas música de bajo riesgo y que siempre
                        cumpla con la normativa.
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Signe 5&nbsp;: Vous recherchez de la musique à faible
                        risque et toujours conforme aux réglementations.
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        징후
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        5:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        당신은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        위험
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        부담이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        적고
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        항상
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        규정을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        준수하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        원합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Subscription-based music licensing companies guarantee
                      compliance. If you’re audited, your subscription agreement
                      proves you’re covered. Traditional licensing leaves you
                      vulnerable if you’ve missed a required license. Read these
                      for select best music&nbsp;—
                      <br />
                      <a
                        className={classes.aHref}
                        href={FIVE_MISTAKES_ARTICLE_URL}
                        title={
                          'Five Mistakes Businesses Make When Choosing Background Music - Practical Stream'
                        }
                      >
                        Mistakes Businesses Make When Choosing
                        Background&nbsp;Music
                      </a>
                    </>,
                    <>
                      Las empresas de licencias musicales por suscripción
                      garantizan el cumplimiento de la normativa. Si te someten
                      a una auditoría, tu contrato de suscripción demuestra que
                      cumples con los requisitos. Las licencias tradicionales te
                      dejan expuesto si te falta alguna licencia obligatoria.
                      Lee estos artículos para elegir la mejor música:&nbsp;
                      <br />
                      <a
                        className={classes.aHref}
                        href={FIVE_MISTAKES_ARTICLE_URL_ES}
                        title={
                          'Cinco errores que cometen las empresas al elegir música de fondo - Practical Stream'
                        }
                      >
                        Errores que cometen las empresas al elegir música
                        de&nbsp;fondo
                      </a>
                    </>,
                    <>
                      Subscription-based music licensing companies guarantee
                      compliance. In the event of an audit, your subscription
                      agreement proves that you are in compliance. Traditional
                      licenses expose you to risks if you have failed to obtain
                      a required license. Refer to this information to choose
                      the best music for your needs&nbsp;—
                      <br />
                      <a
                        className={classes.aHref}
                        href={FIVE_MISTAKES_ARTICLE_URL_FR}
                        title={
                          "Cinq erreurs que les entreprises commettent lors du choix de la musique d'ambiance. - Practical Stream"
                        }
                      >
                        Erreurs que les entreprises commettent lors du choix de
                        la musique d'ambiance
                      </a>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        구독
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        기반
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        라이선스
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        회사는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        규정
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        준수를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        보장합니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        감사
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        대상이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        되더라도
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        구독
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        계약서가
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        라이선스
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        취득
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사실을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        증명해
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        줍니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        기존의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        라이선스
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        방식은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        필요한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        라이선스를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        놓쳤을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        경우
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        취약점이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        발생할
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        수
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있습니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        최고의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        고르려면
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        다음
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        글들을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        읽어보세요
                      </span>
                      &nbsp;—
                      <br />
                      <a
                        className={classes.aHref}
                        href={FIVE_MISTAKES_ARTICLE_URL_KO}
                        title={
                          '기업들이 배경 음악을 선택할 때 저지르는 다섯 가지 실수 - Practical Stream'
                        }
                      >
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          기업들이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          배경
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          선택할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          때
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          저지르는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          실수들
                        </span>
                      </a>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
            </section>
            <section id="decision-guide-on-which-should-you-choose">
              <h2 className={classes.subSection}>
                {translate(
                  <>Decision Guide: Which Should You Choose?</>,
                  <>Guía de decisión: ¿Cuál deberías elegir?</>,
                  <>Guide de décision : Lequel devriez-vous choisir&nbsp;?</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      결정
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      가이드:
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      어떤
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      선택해야
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      할까요?
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <section id="choose-subscription-based-when">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>Choose Subscription-Based If:</>,
                    <>Elige la opción de suscripción si:</>,
                    <>Choisissez l'abonnement si&nbsp;:</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다음과
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        같은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        경우
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        구독
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        기반
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        서비스를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        선택하세요.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>You use music regularly (daily or weekly)</>,
                      <>
                        Utilizas música con regularidad (diariamente o
                        semanalmente).
                      </>,
                      <>
                        Vous écoutez de la musique régulièrement
                        (quotidiennement ou hebdomadairement).
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          당신은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          정기적으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          (매일
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          또는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          매주)
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          듣습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>You have multiple locations</>,
                      <>Tienes varias ubicaciones.</>,
                      <>Vous avez plusieurs emplacements.</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          귀하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          여러
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지점을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          보유하고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>You want simple, all-inclusive legal coverage</>,
                      <>
                        Usted desea una cobertura legal sencilla y con todo
                        incluido.
                      </>,
                      <>
                        Vous souhaitez une couverture juridique simple et
                        complète.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          간단하고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          모든
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          것을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          포함하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          법률
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          서비스를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          원하시나요?
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Budget predictability matters</>,
                      <>La previsibilidad presupuestaria es importante.</>,
                      <>La prévisibilité budgétaire est importante.</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          예산
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          예측
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가능성은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          중요합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>You lack the time to manage licensing complexity</>,
                      <>
                        No tienes tiempo para gestionar la complejidad de las
                        licencias.
                      </>,
                      <>
                        Vous manquez de temps pour gérer la complexité des
                        licences.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          당신은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          복잡한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          라이선스
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          관리를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          처리할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시간이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          부족합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="choose-traditional-licensing-when">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>Choose Traditional If:</>,
                    <>Elige la opción tradicional si:</>,
                    <>Choisissez l'option traditionnelle si&nbsp;:</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다음과
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        같은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        경우
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        전통적인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        방식을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        선택하세요.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        You need a specific chart-topping song for a campaign
                      </>,
                      <>
                        Necesitas una canción que sea un éxito en las listas de
                        popularidad para una campaña.
                      </>,
                      <>
                        Vous avez besoin d'une chanson spécifique qui figure en
                        tête des classements pour votre campagne.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          캠페인을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위해서는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          차트
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          1위를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          차지할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          만한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          특별한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          곡이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          필요합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Your music use is infrequent or project-based</>,
                      <>
                        El uso que haces de la música es poco frecuente o se
                        basa en proyectos específicos.
                      </>,
                      <>
                        Votre utilisation de la musique est occasionnelle ou
                        liée à des projets spécifiques.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          귀하의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          빈도가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          낮거나
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          프로젝트
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기반으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용됩니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        You have the budget and expertise to negotiate
                        individual licenses
                      </>,
                      <>
                        Ustedes cuentan con el presupuesto y la experiencia
                        necesarios para negociar licencias individuales.
                      </>,
                      <>
                        Vous disposez du budget et de l'expertise nécessaires
                        pour négocier des licences individuelles.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          귀사는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          개별
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          라이선스를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          협상할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          예산과
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          전문성을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          갖추고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        You’re creating film, TV, or advertising content
                        requiring particular tracks
                      </>,
                      <>
                        Estás creando contenido para cine, televisión o
                        publicidad que requiere pistas musicales específicas.
                      </>,
                      <>
                        Vous créez du contenu pour le cinéma, la télévision ou
                        la publicité nécessitant des morceaux de musique
                        spécifiques.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          귀하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          특정
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          트랙이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          필요한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          영화,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          TV
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          또는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          광고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          콘텐츠를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          제작하고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
            </section>
            <section id="aspects-of-subscription-vs-traditional">
              <h2 className={classes.subSection}>
                {translate(
                  <>Quick Comparison Table</>,
                  <>Tabla comparativa rápida</>,
                  <>Tableau comparatif rapide</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      빠른 비교표
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <div className={classes.dataTableWrapper}>
                <table className={classes.dataTable}>
                  <caption className={classes.dtCaption}>
                    {translate(
                      <>Music Licensing: Subscription vs. Traditional</>,
                      <>
                        Licencias de música: Modelo de suscripción o modelo
                        tradicional.
                      </>,
                      <>
                        Licences musicales&nbsp;: Abonnement ou modèle
                        traditionnel
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음악 라이선싱: 구독 모델 또는 기존 모델.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col" className={classes.dataTableHeader}>
                        {translate(
                          <>Aspect</>,
                          <>Aspecto</>,
                          <>Dimension</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              차원
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </th>
                      <th scope="col" className={classes.dataTableHeader}>
                        {translate(
                          <>Subscription-Based</>,
                          <>Basado en suscripción</>,
                          <>Basé sur un abonnement</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              구독
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              기반
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </th>
                      <th scope="col" className={classes.dataTableHeader}>
                        {translate(
                          <>Traditional Licensing</>,
                          <>Licencias tradicionales</>,
                          <>Licences traditionnelles</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              전통적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              라이선스
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Pricing model</>,
                          <>Modelo de precios</>,
                          <>Modèle de tarification</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              가격
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              책정
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              모델
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Fixed recurring fee (monthly or annual)</>,
                          <>Tarifas fijas recurrentes (mensuales o anuales)</>,
                          <>Frais récurrents fixes (mensuels ou annuels)</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              정기적으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              발생하는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              고정
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수수료(월별
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              또는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              연간)
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>One-time or per-use fees</>,
                          <>Tarifas únicas o tarifas por uso</>,
                          <>Frais uniques ou frais par utilisation</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              일회성
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              또는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용량별
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              요금
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Cost predictability</>,
                          <>Previsibilidad de costos</>,
                          <>Prévisibilité des coûts</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              비용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              예측
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              가능성
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>High; same fee regardless of usage</>,
                          <>
                            Alto; la tarifa es la misma independientemente del
                            uso
                          </>,
                          <>
                            Élevé ; le tarif est le même quel que soit
                            l'utilisation
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              요금이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              높습니다.
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용량에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              관계없이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              요금은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              동일합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Low; costs vary by song, duration, audience size</>,
                          <>
                            Bajo; los costos varían según la canción, la
                            duración y el tamaño de la audiencia
                          </>,
                          <>
                            Faible&nbsp;; les coûts varient en fonction de la
                            chanson, de la durée et de la taille du public
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              비용은
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              저렴한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              편이며,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              노래,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              길이,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              관객
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              규모에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              따라
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              달라집니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Music access</>,
                          <>Acceso a la música</>,
                          <>Accès à la musique</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              음악
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              접근
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Large pre-cleared catalog included</>,
                          <>
                            Se incluye un amplio catálogo de productos
                            preaprobados
                          </>,
                          <>Un vaste catalogue pré-approuvé est inclus</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              사전에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              승인된
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              제품들의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              종합적인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              목록이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              포함되어
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있습니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>
                            Individual tracks or catalogs licensed separately
                          </>,
                          <>
                            Las pistas o catálogos individuales se licencian por
                            separado
                          </>,
                          <>
                            Les morceaux ou catalogues individuels font l'objet
                            de licences distinctes
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              개별
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              트랙
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              또는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              카탈로그는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              별도로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              라이선스가
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              부여됩니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Licensing complexity</>,
                          <>Complejidad de las licencias</>,
                          <>Complexité des licences</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              라이선스
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              부여의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              복잡성
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>
                            Simple; licenses bundled and managed by provider{' '}
                          </>,
                          <>
                            Es sencillo. Las licencias son gestionadas en
                            paquetes por el proveedor
                          </>,
                          <>
                            C'est simple. Les licences sont gérées par le
                            fournisseur sous forme de forfaits
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              간단합니다.
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              라이선스는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공급업체에서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              묶어서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              관리합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>
                            Complex; multiple rights (sync, public performance,
                            master) negotiated separately
                          </>,
                          <>
                            Complejo; varios derechos (sincronización, ejecución
                            pública, grabación original) se negocian por
                            separado.
                          </>,
                          <>
                            Complexe&nbsp;; plusieurs droits (synchronisation,
                            exécution publique, enregistrement original)
                            négociés séparément
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              복잡함;
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              여러
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              권리(동기화,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공개
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공연, 원음
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              녹음)는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              별도로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              협상됩니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                    </tr>

                    <tr>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Compliance risk</>,
                          <>Riesgo de cumplimiento</>,
                          <>Risque de conformité</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              규정
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              준수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              위험
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Low; provider handles rights and renewals</>,
                          <>
                            Bajo; el proveedor gestiona los derechos y las
                            renovaciones
                          </>,
                          <>
                            Faible&nbsp;; le fournisseur gère les droits et les
                            renouvellements
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              낮음;
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              공급업체가
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              권리
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              및
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              갱신을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              관리합니다
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>
                            Higher; user must ensure all required rights are
                            secured
                          </>,
                          <>
                            Nivel superior; el usuario debe asegurarse de que se
                            hayan obtenido todos los derechos necesarios
                          </>,
                          <>
                            Niveau supérieur&nbsp;; l'utilisateur doit s'assurer
                            que tous les droits requis sont obtenus
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              위험성이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              더
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              높습니다.
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용자는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              필요한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              모든
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              권한을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              확보했는지
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              확인해야
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Scalability</>,
                          <>Escalabilidad</>,
                          <>Évolutivité</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              확장성
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>
                            Easy; supports multiple locations or channels under
                            one plan
                          </>,
                          <>
                            Es conveniente. Un único plan de precios cubre
                            múltiples sitios web o canales
                          </>,
                          <>
                            C'est pratique. Un seul plan tarifaire couvre
                            plusieurs sites ou canaux
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              간편합니다.
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              하나의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              요금제로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              여러
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              위치
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              또는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              채널을
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              지원합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>
                            Difficult; each new use or location may require new
                            licenses
                          </>,
                          <>
                            Es difícil; cada nuevo uso o ubicación puede
                            requerir nuevas licencias
                          </>,
                          <>
                            C'est compliqué&nbsp;; chaque nouvelle utilisation
                            ou chaque nouvel emplacement peut nécessiter de
                            nouvelles licences
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              상황이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              복잡합니다.
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              새로운
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              용도나
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              새로운
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              장소마다
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              새로운
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              라이선스가
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              필요할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있습니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Usage scope</>,
                          <>Ámbito de uso</>,
                          <>Champ d'application</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              사용
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              범위
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>
                            Typically covers in-store, online, and commercial
                            use within plan limits
                          </>,
                          <>
                            Generalmente cubre las compras realizadas en tiendas
                            físicas y en línea, así como el uso comercial,
                            dentro de los límites del plan
                          </>,
                          <>
                            Couvre généralement les achats en magasin, en ligne
                            et l'utilisation commerciale, dans les limites du
                            forfait
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              서비스는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              일반적으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              오프라인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              매장과
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              온라인
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              상점에서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              이루어진
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              구매뿐만
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              아니라,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              요금제
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              한도
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              내에서
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              상업적
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              용도로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용되는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              경우에도
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              적용됩니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Limited to specific uses defined in each license</>,
                          <>
                            Su uso está restringido a los fines específicos
                            indicados en cada licencia
                          </>,
                          <>
                            Son utilisation est limitée aux fins spécifiques
                            indiquées dans chaque licence
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              각
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              라이선스에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              명시된
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              특정
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              용도로만
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용이
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              제한됩니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Administration effort</>,
                          <>Esfuerzo administrativo</>,
                          <>Effort administratif</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              행정적
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              노력
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Minimal</>,
                          <>Mínima</>,
                          <>Minimum</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              최소한의
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>High</>,
                          <>Alta</>,
                          <>Haut</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              높은
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Flexibility</>,
                          <>Flexibilidad</>,
                          <>Flexibilité</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              유연성
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Limited to provider’s catalog</>,
                          <>Limitado al catálogo del proveedor</>,
                          <>Limité au catalogue du fournisseur</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              공급업체의
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              카탈로그에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있는
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              제품으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              제한됩니다
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>High; can license any commercially released song</>,
                          <>
                            Alta; se pueden obtener licencias para todas las
                            canciones publicadas comercialmente
                          </>,
                          <>
                            Haut&nbsp;; des licences peuvent être obtenues pour
                            toutes les chansons commercialisées.
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              높음;
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              상업적으로
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              발매된
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              모든
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              곡에
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              대한
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              라이선스를
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              취득할
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              수
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              있습니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>Typical users</>,
                          <>Usuarias tipicas</>,
                          <>Utilisateurs typiques</>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              일반
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              사용자
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>
                            Businesses, retail chains, restaurants, gyms,
                            digital platforms
                          </>,
                          <>
                            Empresas, cadenas minoristas, restaurantes,
                            gimnasios, plataformas digitales
                          </>,
                          <>
                            Entreprises, chaînes de magasins, restaurants,
                            salles de sport, plateformes numériques
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              기업,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              소매
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              체인점,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              레스토랑,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              헬스장, 디지털
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              플랫폼
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                      <td className={classes.dataTableField}>
                        {translate(
                          <>
                            Film studios, advertisers, broadcasters, one-off
                            campaigns
                          </>,
                          <>
                            Estudios de cine, anunciantes, cadenas de
                            televisión, campañas puntuales
                          </>,
                          <>
                            Studios de cinéma, annonceurs, chaînes de
                            télévision, campagnes ponctuelles
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              영화
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              제작사,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              광고주,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              텔레비전
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              채널,
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              단발성
                            </span>{' '}
                            <span
                              className={classes.asianTextBlock}
                              aria-hidden={true}
                            >
                              캠페인
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                      <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                      <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
            <section id="common-business-scenarios">
              <h2 className={classes.subSection}>
                {translate(
                  <>Common Business Scenarios</>,
                  <>Escenarios comerciales comunes</>,
                  <>Scénarios commerciaux courants</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일반적인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비즈니스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      시나리오
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    <span className={classes.spanBolder}>
                      Small Café Owner:
                    </span>{' '}
                    A subscription from music licensing companies for
                    restaurants costs $30/month, covers all legal requirements,
                    and provides curated playlists. Perfect fit.
                  </>,
                  <>
                    <span className={classes.spanBolder}>
                      Dueño de una pequeña cafetería:
                    </span>{' '}
                    Una suscripción a empresas de licencias musicales para
                    restaurantes cuesta 30 dólares al mes, cubre todos los
                    requisitos legales y ofrece listas de reproducción
                    seleccionadas. La solución perfecta.
                  </>,
                  <>
                    <span className={classes.spanBolder}>
                      Propriétaire d'un petit café&nbsp;:
                    </span>{' '}
                    Un abonnement auprès de sociétés de licences musicales pour
                    les restaurants coûte 30 $ par mois, couvre toutes les
                    exigences légales et propose des listes de lecture
                    personnalisées. C'est parfait.
                  </>,
                  <>
                    <span className={classes.spanBolder}>소규모</span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      카페
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사장님:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      저작권
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      관리
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      회사에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      제공하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      레스토랑용
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구독
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      서비스는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      월
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      30달러이며,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      모든
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      법적
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      요구
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사항을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      충족하고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      엄선된
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      플레이리스트를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      제공합니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      완벽한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      선택입니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    <span className={classes.spanBolder}>
                      Marketing Agency:
                    </span>{' '}
                    Needs a specific Ed Sheeran track for a client’s ad
                    campaign. Traditional licensing makes sense here — pay once
                    for that specific use.
                  </>,
                  <>
                    <span className={classes.spanBolder}>
                      Agencia de marketing:
                    </span>{' '}
                    Necesita una canción específica de Ed Sheeran para la
                    campaña publicitaria de un cliente. En este caso, la
                    concesión de licencias tradicional es la opción más
                    adecuada: pagar una sola vez por ese uso específico.
                  </>,
                  <>
                    <span className={classes.spanBolder}>
                      Agence de marketing&nbsp;:
                    </span>{' '}
                    A besoin d'un titre spécifique d'Ed Sheeran pour la campagne
                    publicitaire d'un client. Une licence traditionnelle est la
                    solution idéale dans ce cas : un paiement unique pour cette
                    utilisation spécifique.
                  </>,
                  <>
                    <span className={classes.spanBolder}>마케팅</span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대행사:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      고객의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      광고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      캠페인을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      위해
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      특정
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      에드
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      시런(Ed
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Sheeran)
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      곡이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      필요합니다. 이런
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      경우에는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      기존의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      방식이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      적합합니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      해당
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      곡을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      특정
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      용도로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사용하기
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      위해
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      번만
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비용을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지불하면
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      됩니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    <span className={classes.spanBolder}>
                      Regional Gym Chain (5 Locations):
                    </span>{' '}
                    Subscription wins. Pay around $150/month total, manage
                    everything from one dashboard, and stay compliant across all
                    sites.
                  </>,
                  <>
                    <span className={classes.spanBolder}>
                      Cadena regional de gimnasios (5 ubicaciones):
                    </span>{' '}
                    Ventajas de la suscripción: Pague alrededor de $150 al mes
                    en total, gestione todo desde un único panel de control y
                    cumpla con la normativa en todas las sucursales.
                  </>,
                  <>
                    <span className={classes.spanBolder}>
                      Chaîne de salles de sport régionales (5 sites)&nbsp;:
                    </span>{' '}
                    Abonnement avantageux. Payez environ 150 $ par mois au
                    total, gérez tout depuis un seul tableau de bord et restez
                    en conformité sur tous les sites.
                  </>,
                  <>
                    <span className={classes.spanBolder}>지역</span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      체인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      헬스장
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      (5개
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지점):
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      구독
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      서비스가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      정답입니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      월
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      약
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      150달러만
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지불하면
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      모든
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하나의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대시보드에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      관리하고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      모든
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사이트에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      규정을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      준수할
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      수
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="concluding-remarks">
              <h2 className={classes.subSection}>
                {translate(
                  <>Final Recommendation</>,
                  <>Recomendación final</>,
                  <>Recommandation finale</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      최종
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      권장
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사항
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    For most businesses using music regularly in commercial
                    spaces, subscription-based music licensing services offer
                    the best combination of affordability, simplicity, and legal
                    protection. You get unlimited music, comprehensive rights
                    coverage, and peace of mind&nbsp;— all for a predictable
                    monthly cost.
                  </>,
                  <>
                    Para la mayoría de las empresas que utilizan música
                    regularmente en sus locales comerciales, los servicios de
                    licencias musicales por suscripción ofrecen la mejor
                    combinación de precio, sencillez y protección legal.
                    Obtendrá música ilimitada, cobertura integral de derechos y
                    total tranquilidad, todo por un coste mensual predecible.
                  </>,
                  <>
                    Pour la plupart des entreprises qui utilisent régulièrement
                    de la musique dans leurs locaux commerciaux, les services de
                    licence musicale par abonnement offrent la meilleure
                    combinaison de prix abordable, de simplicité et de
                    protection juridique. Vous bénéficiez d'une musique
                    illimitée, d'une couverture complète des droits et d'une
                    tranquillité d'esprit, le tout pour un coût mensuel
                    prévisible.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      상업
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      공간에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      정기적으로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      사용하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대부분의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      기업에게는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구독
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      기반
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      서비스가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      경제성,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      간편성,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      법적
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      보호
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      측면에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      최상의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      선택입니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      무제한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이용,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      포괄적인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      저작권
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      보호,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      그리고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      마음의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      평안을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      모두
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      예측
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      가능한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      월별
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비용으로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      누릴
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      수
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Traditional licensing still has its place for specific,
                    one-time projects or when you absolutely need particular
                    popular songs. But for day-to-day business operations,
                    subscriptions are the clear winner.
                  </>,
                  <>
                    Las licencias tradicionales todavía tienen su utilidad para
                    proyectos específicos y puntuales, o cuando se necesitan
                    determinadas canciones populares. Pero para las operaciones
                    comerciales diarias, las suscripciones son la opción más
                    ventajosa.
                  </>,
                  <>
                    Les licences traditionnelles conservent leur utilité pour
                    des projets spécifiques et ponctuels, ou lorsque vous avez
                    absolument besoin de certaines chansons populaires. Mais
                    pour les opérations commerciales quotidiennes, les
                    abonnements sont clairement la solution la plus avantageuse.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전통적인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      방식은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      특정
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      일회성
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      프로젝트나
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      특정
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      인기곡이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      반드시
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      필요한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      경우에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      여전히
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      유용합니다.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하지만
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      일상적인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비즈니스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      운영에는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구독
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      방식이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      확실히
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      더
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      유리합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Don’t risk costly fines or legal trouble. Explore
                    subscription-based music licensing companies today, choose a
                    plan that fits your business size and industry, and enjoy
                    compliant, quality music that enhances your customer
                    experience.
                  </>,
                  <>
                    No se arriesgue a multas costosas ni a problemas legales.
                    Explore hoy mismo las empresas de licencias de música por
                    suscripción, elija un plan que se adapte al tamaño y al
                    sector de su negocio y disfrute de música de calidad que
                    cumple con la normativa y mejora la experiencia de sus
                    clientes.
                  </>,
                  <>
                    Ne prenez pas le risque d'encourir des amendes coûteuses ou
                    des problèmes juridiques. Découvrez dès aujourd'hui les
                    services de licence musicale par abonnement, choisissez une
                    formule adaptée à la taille et au secteur d'activité de
                    votre entreprise et profitez d'une musique de qualité,
                    conforme à la réglementation, qui améliore l'expérience de
                    vos clients.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      값비싼
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      벌금이나
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      법적
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      문제의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      위험을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      감수하지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      마세요.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      지금
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      바로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구독
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      기반
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      회사를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      알아보시고,
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      귀사의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      규모와
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      업종에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      맞는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      플랜을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      선택하여
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      고객
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      경험을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      향상시키는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      합법적이고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      고품질의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      즐기세요.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    <span className={classes.spanBolder}>Your next step?</span>{' '}
                    Compare providers, start a free trial, and make 2026 the
                    year your business gets music licensing right.
                  </>,
                  <>
                    <span className={classes.spanBolder}>
                      ¿Cuál es tu siguiente paso?
                    </span>{' '}
                    Compara proveedores, inicia una prueba gratuita y haz que
                    2026 sea el año en que tu negocio gestione correctamente las
                    licencias musicales.
                  </>,
                  <>
                    <span className={classes.spanBolder}>
                      Quelle est la prochaine étape ?
                    </span>{' '}
                    Comparez les fournisseurs, commencez un essai gratuit et
                    faites de 2026 l'année où votre entreprise gérera
                    correctement les licences musicales.
                  </>,
                  <>
                    <span className={classes.spanBolder}>다음</span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      단계는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      무엇일까요?
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다양한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      제공업체를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비교해
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      보고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      무료
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      체험을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      시작하여
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      2026년을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      귀사의
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      비즈니스가
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      라이선스를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      제대로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      활용하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      해로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      만드세요.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <hr className={classes.threeDotDivisorBottomOfArticle} />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
            <br />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <ReadyToTransform
              selectedLanguage={selectedLanguage}
              linkVariant={false}
            />
          </Grid.Col>
          <Grid.Col md={24}>
            <div>
              <br />
              <br />
              <br />
            </div>
          </Grid.Col>
        </Grid>
      </div>
    </div>
  )
}
